import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  List,
  ListItem,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
} from '@mui/material';
import {
  Work,
  LocationOn,
  Timer,
  School,
  CheckCircle,
  Send,
  ArrowForward,
  BusinessCenter,
  EmojiObjects,
  GroupAdd,
  TrendingUp,
  CloudUpload,
  LinkedIn,
  GitHub,
  Email,
  Phone,
  AccessTime,
  MonetizationOn,
  WorkHistory,
  EmojiEmotions,
  Groups,
  Public,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useCareer } from '../context/CareerContext';
import Link from '@mui/material/Link';

const benefits = [
  {
    icon: <BusinessCenter sx={{ fontSize: 40 }} />,
    title: "Professional Growth",
    description: "Continuous learning and development opportunities",
    color: "#2196F3"
  },
  {
    icon: <EmojiObjects sx={{ fontSize: 40 }} />,
    title: "Innovation Culture",
    description: "Work with cutting-edge water treatment technologies",
    color: "#4CAF50"
  },
  {
    icon: <GroupAdd sx={{ fontSize: 40 }} />,
    title: "Collaborative Environment",
    description: "Be part of a dynamic and supportive team",
    color: "#FF9800"
  },
  {
    icon: <TrendingUp sx={{ fontSize: 40 }} />,
    title: "Career Advancement",
    description: "Clear path for growth and promotions",
    color: "#9C27B0"
  }
];

// Helper function for time ago
function getTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
}

const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { jobs, loading, error, addApplication } = useCareer();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    resume: null,
    coverLetter: '',
    linkedIn: '',
    github: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [activeJobId, setActiveJobId] = useState(null);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
    setApplicationForm({
      name: '',
      email: '',
      phone: '',
      experience: '',
      resume: null,
      coverLetter: '',
      linkedIn: '',
      github: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addApplication({
        ...applicationForm,
        jobId: selectedJob.id,
        position: selectedJob.title
      });
      setSnackbar({
        open: true,
        message: 'Application submitted successfully! We will contact you soon.',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to submit application. Please try again.',
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const activeJobs = jobs.filter(job => job.status === 'Active');

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '390px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/images/carrer.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          color: 'white',
          textAlign: 'center',
          mb: 0,
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            pl: { xs: 0, md: 0 }
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ marginLeft: '-1rem' }}
          >
            <Box
              sx={{
                maxWidth: '600px',
                textAlign: 'left',
                position: 'relative',
                p: 4,
                ml: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)',
                backdropFilter: 'blur(8px)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(to bottom, #0288d1, transparent)',
                  borderRadius: '4px 0 0 4px',
                }
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #0288d1, rgba(2,136,209,0.2))',
                    borderRadius: '2px',
                  }
                }}
              >
                Join Our Team
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  maxWidth: '500px',
                  opacity: 0.9,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  pl: 0.5
                }}
              >
                Be part of a team that's revolutionizing water treatment technology and making a positive impact on the environment
              </Typography>
            </Box>
          </motion.div>
        </Container>

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)',
            pointerEvents: 'none'
          }}
        />
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ mb: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #1a237e 30%, #0288d1 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'linear-gradient(90deg, #1a237e 30%, #0288d1 90%)'
                }
              }}
            >
              Why Join Us?
            </Typography>
            <Grid container spacing={3}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: '16px',
                        background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        border: '1px solid',
                        borderColor: 'grey.100',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 12px 24px ${benefit.color}25`,
                          '& .benefit-icon': {
                            transform: 'scale(1.1)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${benefit.color}, ${benefit.color}50)`
                        }
                      }}
                    >
                      <Box
                        className="benefit-icon"
                        sx={{
                          mb: 2,
                          display: 'inline-flex',
                          p: 1.5,
                          borderRadius: '12px',
                          bgcolor: `${benefit.color}15`,
                          color: benefit.color,
                          transition: 'transform 0.3s ease'
                        }}
                      >
                        {benefit.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: '1.1rem',
                          mb: 1
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontSize: '0.9rem'
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>

        {/* Open Positions */}
        <Box id="open-positions" sx={{ mb: 8, py: 6, backgroundColor: '#f8fafc' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h2"
                align="center"
                sx={{
                  mb: 1,
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: '#1a237e',
                }}
              >
                Open Positions
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  mb: 6,
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                Join our team and make a difference in the world
              </Typography>
              {activeJobs.length === 0 ? (
                <Box 
                  sx={{ 
                    mt: 4, 
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    p: 4,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No open positions at the moment.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Please check back later for new opportunities.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {activeJobs.map((job) => (
                    <Grid item xs={12} key={job.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          p: 3,
                          mb: 3,
                          background: '#fff',
                          border: '1px solid #e3e8ee',
                          boxShadow: '0 2px 8px 0 rgba(16,30,54,.04)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          position: 'relative',
                          transition: 'box-shadow 0.2s, border-color 0.2s',
                          '&:hover': {
                            boxShadow: '0 4px 16px 0 rgba(16,30,54,.08)',
                            borderColor: '#b6c6e3'
                          },
                        }}
                      >
                        {/* Time ago on top right */}
                        <Typography
                          variant="body2"
                          sx={{
                            position: 'absolute',
                            top: 20,
                            right: 24,
                            color: 'grey.400',
                            fontSize: 14,
                            fontWeight: 400
                          }}
                        >
                          {getTimeAgo(job.postedDate)}
                        </Typography>
                        {/* Company Logo */}
                        <Avatar
                          src={job.companyLogo}
                          sx={{ width: 48, height: 48, mr: 2, bgcolor: '#f5f6fa', fontSize: 22 }}
                        >
                          {(job.companyName || 'C').charAt(0)}
                        </Avatar>
                        {/* Main Info + Button */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' } }}>
                          {/* Info Section */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: '#222' }}>
                              {job.title}
                            </Typography>
                            {job.companyName && (
                              <Typography variant="subtitle2" sx={{ color: '#7b8a9c', fontWeight: 500, mb: 1 }}>
                                {job.companyName}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                              <Chip
                                icon={<LocationOn sx={{ fontSize: 16, color: '#90a4ae' }} />}
                                label={job.location}
                                size="small"
                                sx={{ bgcolor: '#f4f6f8', color: '#607d8b', fontWeight: 500, px: 1.5, borderRadius: 2, fontSize: 14 }}
                              />
                              {job.salary && (
                                <Chip
                                  icon={<MonetizationOn sx={{ fontSize: 16, color: '#a5d6a7' }} />}
                                  label={job.salary}
                                  size="small"
                                  sx={{ bgcolor: '#f4f6f8', color: '#388e3c', fontWeight: 500, px: 1.5, borderRadius: 2, fontSize: 14 }}
                                />
                              )}
                              {job.remote && (
                                <Chip
                                  icon={<Public sx={{ fontSize: 16, color: '#ffe082' }} />}
                                  label="Remote"
                                  size="small"
                                  sx={{ bgcolor: '#f4f6f8', color: '#bfa700', fontWeight: 500, px: 1.5, borderRadius: 2, fontSize: 14 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              {(Array.isArray(job.skills) ? job.skills : (job.skills ? job.skills.split(',').map(s => s.trim()) : [])).map((skill, idx) => (
                                <Chip
                                  key={idx}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f4f6f8',
                                    color: '#7b8a9c',
                                    fontWeight: 400,
                                    borderRadius: 2,
                                    fontSize: 13
                                  }}
                                />
                              ))}
                            </Box>
                            {/* Mini Description */}
                            {job.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 15, lineHeight: 1.6 }}>
                                {job.description.length > 120 ? job.description.slice(0, 120) + '...' : job.description}
                              </Typography>
                            )}
                          </Box>
                          {/* Apply Button */}
                          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'flex-end', mt: { xs: 2, md: 0 }, ml: { md: 3 }, width: { xs: '100%', md: 'auto' } }}>
                            <Button
                              variant="contained"
                              size="medium"
                              sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.2,
                                fontWeight: 700,
                                fontSize: 16,
                                background: '#1976d2',
                                color: '#fff',
                                boxShadow: 'none',
                                textTransform: 'none',
                                letterSpacing: 0.2,
                                transition: 'background 0.2s',
                                '&:hover': {
                                  background: '#115293',
                                },
                                width: { xs: '100%', md: 'auto' },
                                minWidth: 120
                              }}
                              onClick={() => handleApply(job)}
                            >
                              Apply
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </motion.div>
          </Container>
        </Box>
      </Container>

      {/* Application Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="div" fontWeight={600}>
            Apply for {selectedJob?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="name"
                  label="Full Name"
                  value={applicationForm.name}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={applicationForm.email}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="phone"
                  label="Phone"
                  value={applicationForm.phone}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="experience"
                  label="Years of Experience"
                  value={applicationForm.experience}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="linkedIn"
                  label="LinkedIn Profile URL"
                  value={applicationForm.linkedIn}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <LinkedIn sx={{ color: '#0077b5', mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="github"
                  label="GitHub Profile URL"
                  value={applicationForm.github}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <GitHub sx={{ color: '#333', mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="resume-upload"
                    onChange={(e) => {
                      setApplicationForm(prev => ({
                        ...prev,
                        resume: e.target.files[0]
                      }));
                    }}
                  />
                  <label htmlFor="resume-upload">
                    <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body1" gutterBottom>
                      {applicationForm.resume
                        ? `Selected: ${applicationForm.resume.name}`
                        : 'Drop your resume here or click to upload'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supported formats: PDF, DOC, DOCX (Max size: 5MB)
                    </Typography>
                  </label>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="coverLetter"
                  label="Cover Letter"
                  value={applicationForm.coverLetter}
                  onChange={handleInputChange}
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !applicationForm.name ||
              !applicationForm.email ||
              !applicationForm.phone ||
              !applicationForm.experience ||
              !applicationForm.resume
            }
            sx={{
              borderRadius: '28px',
              px: 4,
              background: 'linear-gradient(45deg, #1a237e 30%, #0288d1 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0288d1 30%, #1a237e 90%)',
              }
            }}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Careers;
